import { useMemoTranslations } from 'hooks/Intl';
import { useEffect, useState } from 'react';
import * as translations from 'intls/medicine';
import { useFormik } from 'formik';
import Select from 'components/Select';
import { medicineService } from 'services';
import styles from './index.module.scss';
import medicineStyles from '../index.module.scss';
import { useTheme } from 'hooks/theme';
import Typography from 'components/Typography';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { useMedicine } from 'hooks/Medicine';
import Input from 'components/Input';
import useDebounce from 'hooks/useDebounce';

interface EquivalencyListProps {
  medicine: Medicine;
}

const EquivalencyList = ({ medicine }: EquivalencyListProps) => {
  const { theme } = useTheme();
  const intl = useMemoTranslations(translations);

  const [equivalentMedicines, setEquivalentMedicines] = useState<Medicine[]>(
    []
  );
  const [fetchStatus, setFetchStatus] = useState<LoadingStatus>('NOT_LOADED');

  const { countryOptions } = useMedicine();

  const formik = useFormik({
    initialValues: { country: '', query: '' },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
  });

  const fetchMedicines = async (
    atcCode: string,
    country: string,
    query: string
  ) => {
    try {
      setFetchStatus?.('LOADING');
      const { data: medicines } = await medicineService.getAtcCode(atcCode, {
        country,
        q: query || undefined,
      });
      setEquivalentMedicines(medicines);
      setFetchStatus?.('LOADED');
    } catch (error) {
      setFetchStatus?.('ERROR');
    }
  };

  const fetchMedicinesDebounced = useDebounce(
    () =>
      fetchMedicines(
        medicine.atc_code,
        formik.values.country,
        formik.values.query
      ),
    1000
  );

  useEffect(() => {
    const { country } = formik.values;
    if (!country) return;
    fetchMedicines(medicine.atc_code, country, '');
  }, [formik.values.country]);

  useEffect(() => {
    const { country } = formik.values;
    if (!country) return;

    fetchMedicinesDebounced();
  }, [formik.values.query]);

  return (
    <div>
      <div
        className={`${medicineStyles.contentContainer} ${styles.header} ${medicineStyles[theme]}`}
      >
        <Typography className={styles.headerTitle}>
          {intl.FIND_BASED_ON_SUBSTANCE}
        </Typography>
        <div className="row">
          <Select
            label={intl.MEDICINE_COUNTRY_OF_TRAVEL}
            formikProps={formik}
            name="country"
            className="col-12 col-md-4"
          >
            <option value="">{intl.MEDICINE_COUNTRY_OF_TRAVEL}</option>
            {countryOptions.map(option => (
              <option value={option.value} key={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Input
            label={intl.FILTER_MEDICINES}
            formikProps={formik}
            name="query"
            maxLength={55}
            className="col-12 col-md-8"
          />
        </div>
      </div>
      {fetchStatus === 'LOADING' && (
        <div>
          <LoadingSpinner fill />
        </div>
      )}
      {fetchStatus === 'LOADED' && equivalentMedicines.length === 0 && (
        <Typography type="p" color="subtitle" className={styles.productsHeader}>
          {intl.EQUIVALENT_MEDICINE_NOT_FOUND}
        </Typography>
      )}
      {fetchStatus === 'LOADED' && equivalentMedicines.length > 0 && (
        <>
          <Typography
            type="h6"
            className={styles.productsHeader}
            color="primary"
          >
            {intl.PRODUCTS}:
          </Typography>
          <ul>
            {equivalentMedicines.map(medicine => (
              <li key={medicine.medicine_id}>
                <Typography color="subtitle">{medicine.local_name}</Typography>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default EquivalencyList;
